import type { PolicyDocument, PolicySection } from './types'
import { OPERATOR } from './operator'
import {
  CHILDREN,
  CRASHLYTICS_OVERSEAS_ROW,
  CRASHLYTICS_ROW,
  DISPOSAL,
  OFFICER,
  OVERSEAS_HEADERS,
  OVERSEAS_REFUSAL,
  PROCESSOR_HEADERS,
  R2_ITEMS_BOTH,
  R2_RETENTION_BOTH,
  REMEDIES,
  RETENTION_HEADERS,
  REVENUECAT_OVERSEAS_ROW,
  REVENUECAT_ROW,
  SECURITY,
  SPONSOR_ITEM_AUTO,
  SPONSOR_NOTE,
  SPONSOR_PURPOSE,
  SPONSOR_RETENTION_ROW,
  SUPPORT_ITEM_AUTO,
  SUPPORT_ITEM_INPUT,
  SUPPORT_NOTE,
  SUPPORT_PURPOSE,
  SUPPORT_RETENTION_ROW,
  admobOverseasRow,
  admobRow,
  changes,
  cloudflareOverseasRow,
  cloudflareRow,
  rights,
  table,
} from './common'

/**
 * 시험보소 개인정보 처리방침.
 *
 * 이 앱은 **서버가 있다** — 첫장면과 다르다. 관심 등록의 주인이 계정이라 기기를 바꿔도
 * 따라와야 하기 때문이다. 그래서 계정·설치 식별자·푸시 토큰이 서버에 남는다.
 *
 * 2026-08-06 로그인 수단이 늘었다 — 아이디/비밀번호 + 카카오·구글·애플
 * (네이버는 2026-08-07 제외).
 * **비밀번호와 이메일을 우리가 갖게 됐으므로** 제2조·제6조·제7조를 함께 고쳤다.
 * 반대로 사진·문서·위치는 전혀 다루지 않고 AI 도 쓰지 않는다.
 *
 * 일정 자료는 공공데이터(한국산업인력공단 국가자격 API)에서 가져온 공개 정보이고
 * 개인정보가 아니다 — 그래서 처리 항목에 넣지 않고 별도 조항으로 출처만 밝힌다.
 *
 * 앱 이름은 한국어 그대로 둔다 — 스토어 등록명이 `시험보소` 다.
 */

const EFFECTIVE_DATE = 'August 20, 2026'
const LAST_REVISED = 'August 26, 2026'
const CRASH_RETENTION = '90 days'
/** 위탁·국외이전 표의 "무슨 광고인가" 칸. 나머지 칸은 common.ts 가 갖는다 */
const ADMOB_PURPOSE = 'Serving banner ads'

const SECTIONS: readonly PolicySection[] = [
  {
    heading: 'Article 1. Purposes of processing personal data',
    paragraphs: [
      'The Operator processes personal data for the following purposes only. Should a purpose change, the Operator takes the steps required by Article 18 of the Personal Information Protection Act, including obtaining separate consent.',
    ],
    bullets: [
      'Identifying members and keeping them signed in — identifying you by username and password or by a social account, so that the certifications you follow belong to an account',
      'Resetting a password — sending a reset link to a user who has forgotten their password',
      'Sending schedule reminders — telling you when registration opens or closes, when the exam falls, and when results are published for a certification you follow',
      'Syncing settings across devices — carrying the same followed certifications and reminder settings across a device change or several devices',
      'Preventing duplicate delivery — recording that a reminder has gone out so the same one is not sent twice',
      'Running the community — publishing the posts and comments users write, receiving and handling reports, and applying the blocks a user has chosen',
      'Keeping the service reliable — finding and fixing the places where the app errors',
      SUPPORT_PURPOSE,
      SPONSOR_PURPOSE,
    ],
    note: 'Searching for certifications and checking schedules work without signing in. A login is needed only if you want reminders.',
  },
  {
    heading: 'Article 2. Personal data processed',
    paragraphs: ['The items below are everything the Operator processes.'],
    bullets: [
      '[Required] Account identity — your username, and, if you use a social login, the account identifier and profile name that provider issues',
      '[Required] Password — only if you signed up with a username and password. **Stored as a one-way hash, so not even the Operator can read the original**',
      '[Required] Email address — used only for password reset and account verification. If you use a social login only, this follows whatever that provider supplies',
      '[Required] Installation identifier — a random value created for each device the first time the app runs. It is used so that a reminder is sent only once when the same account is used on several devices',
      '[Required] Push token — the address a notification is sent to. It differs per device and becomes invalid when the app is deleted',
      '[Optional] Reminder settings — the certifications you follow, which stages you want reminders for (registration opening, registration closing, exam day, results) and which exam type (written or practical), and how many days ahead',
      '[Optional] Community posts and comments — the title, body, and time of what you wrote. **These are shown to other users together with your profile name as of the time of writing**',
      '[Optional] Block list — the account identifiers of other users you have blocked',
      '[Collected automatically] Report records — the reports you have filed against a post or comment (target and reason)',
      '[Collected automatically] Delivery records — a record that a reminder has been sent, so the same one does not go out twice',
      '[Collected automatically] Device information and advertising identifier used in serving ads — processed by Google (AdMob), which supplies the banner ads, for ad delivery and fraud prevention. The Operator does not receive this information',
      '[Collected automatically] App diagnostics — Firebase Crashlytics installation identifier, app version and build, device model, operating system, error messages, stack traces, and crash traces',
      SUPPORT_ITEM_INPUT,
      SUPPORT_ITEM_AUTO,
      SPONSOR_ITEM_AUTO,
    ],
    note: 'If you use a social login, the Operator never receives that provider’s password. Phone number, date of birth, and gender are not requested by any route. Location data, contacts, payment details, and address books are not collected either, and a photograph is collected only where you attach one to a support message yourself. Neither sensitive data under Article 23 nor unique identifying information under Article 24 of the Personal Information Protection Act is collected. The Operator has no way of knowing whether you actually registered for an exam or passed it.',
  },
  {
    heading: 'Article 3. Processing and retention periods',
    table: table(RETENTION_HEADERS, [
      [
        'Account identity',
        'Until you ask to withdraw (delete your account) — destroyed on request',
        'Your consent',
      ],
      [
        'Password (hash) and email address',
        'Until you ask to withdraw — destroyed on request',
        'Your consent',
      ],
      [
        'Followed certifications and reminder settings',
        'Until you remove the certification or withdraw',
        'Your consent',
      ],
      [
        'Community posts and comments',
        'Until you delete them or withdraw (destroyed immediately on withdrawal)',
        'Your consent',
      ],
      ['Block list', 'Until you unblock or withdraw', 'Your consent'],
      ['Report records', 'Destroyed once the report is handled, or when the reporter withdraws', 'Your consent'],
      [
        'Push token',
        'Until it becomes invalid through app deletion or turning reminders off, or delivery is confirmed to have failed',
        'Your consent',
      ],
      ['Delivery records', 'Destroyed once the exam schedule concerned has passed', 'Your consent'],
      ['App diagnostics', `${CRASH_RETENTION} from collection`, 'Your consent (processor retention cycle)'],
      [...SUPPORT_RETENTION_ROW],
      [...SPONSOR_RETENTION_ROW],
    ]),
    note: `The Operator carries out no transaction that would create a retention duty under the Act on Consumer Protection in Electronic Commerce or similar legislation, so no personal data is kept separately under any statute. ${SUPPORT_NOTE} ${SPONSOR_NOTE}`,
  },
  {
    heading: 'Article 4. Where the exam schedule data comes from',
    paragraphs: [
      'The exam schedules shown in the app are based on the national qualification exam schedule data published by the Human Resources Development Service of Korea (한국산업인력공단) through the national public data portal. That is public information, not personal data, and each schedule is shown together with its source, a link to the original announcement, and the time it was last checked.',
      '시험보소 is not an official app of any examining body and has no affiliation with one. Schedules can change at the examining body’s discretion, so check the original announcement before registering. The app does not register you for an exam; it only opens the registration page in your device browser.',
    ],
  },
  {
    heading: 'Article 5. Provision of personal data to third parties',
    paragraphs: [
      'The Operator processes personal data only within the purposes set out in Article 1 and provides it to third parties only with your consent or where Article 17 or Article 18 of the Personal Information Protection Act applies. Personal data is never sold.',
    ],
  },
  {
    heading: 'Article 6. Entrustment of personal data processing',
    paragraphs: ['To provide the service, the Operator entrusts personal data processing as follows.'],
    table: table(PROCESSOR_HEADERS, [
      [
        'Kakao Corp.',
        'Authenticating a Kakao account sign-in',
        'Account identifier and profile name',
        'Limited to the moment authentication is processed',
      ],
      [
        'Google LLC',
        'Authenticating a Google account sign-in',
        'Account identifier, name, and email address',
        'Limited to the moment authentication is processed',
      ],
      [
        'Apple Inc.',
        'Authenticating an Apple account sign-in',
        'Account identifier and email address (which you may choose to hide)',
        'Limited to the moment authentication is processed',
      ],
      [
        'Google LLC (Firebase Cloud Messaging)',
        'Delivering reminders',
        'Push token, notification title and body (the certification name and schedule stage)',
        'Limited to the moment delivery is processed',
      ],
      admobRow(ADMOB_PURPOSE),
      [...CRASHLYTICS_ROW],
      cloudflareRow(R2_ITEMS_BOTH, R2_RETENTION_BOTH),
      [...REVENUECAT_ROW],
    ]),
    note: 'In each processing agreement, as required by Article 26 of the Personal Information Protection Act, the Operator sets out in writing the prohibition on processing beyond the stated purpose, technical and administrative safeguards, restrictions on sub-processing, supervision, and liability for damages, and supervises whether the processor handles personal data safely. If the processors change, this policy is updated and notice given in advance.',
  },
  {
    heading: 'Article 7. Transfer of personal data abroad',
    paragraphs: [
      'Of the processors named in Article 6, Google LLC and Apple Inc. are located outside Korea, and the entrustment transfers personal data abroad as follows. Kakao Corp. is a Korean company, so no transfer abroad is involved there.',
    ],
    table: table(OVERSEAS_HEADERS, [
      [
        'Google LLC',
        'The United States and other countries where Google operates',
        'Push token, notification title and body',
        'Sent over HTTPS at the moment a reminder is delivered',
        'Delivering reminders',
        'Limited to the moment delivery is processed',
      ],
      [...CRASHLYTICS_OVERSEAS_ROW],
      cloudflareOverseasRow(R2_ITEMS_BOTH, R2_RETENTION_BOTH),
      [...REVENUECAT_OVERSEAS_ROW],
      admobOverseasRow(ADMOB_PURPOSE),
      [
        'Apple Inc.',
        'The United States',
        'Account identifier and email address',
        'Sent over HTTPS at the moment you choose Sign in with Apple',
        'Authenticating an Apple account sign-in',
        'Limited to the moment authentication is processed',
      ],
    ]),
    note: OVERSEAS_REFUSAL,
  },
  DISPOSAL,
  rights(
    [
      'In the app — the certifications you follow and your reminder settings can be viewed, changed, and deleted directly',
      'Deleting posts and comments — you can delete what you wrote from within the app, and withdrawing destroys all of it immediately',
      'Turning reminders off — revoking the notification permission in device settings stops reminders only; search and schedules keep working',
      'Deleting a support message — send us the reference number shown on the support page at the email address below and that message is deleted',
    ],
    'the certifications you follow, your reminder settings, and the text of support messages',
  ),
  {
    heading:
      'Installation and operation of automatic collection devices, and how to refuse them (including advertising)',
    paragraphs: [
      'The service shows a banner ad at the bottom of the screen, served by Google LLC’s AdMob. The Operator requests non-personalized ads only, which means your behavioural data is not used for targeted advertising. Even for non-personalized ads, however, Google may process device information, an advertising identifier (ADID/IDFA), and coarse IP-based location for ad delivery, fraud prevention, and frequency capping. That information goes from your device directly to Google without passing through the Operator, and Google’s handling of it is governed by the Google Privacy Policy (https://policies.google.com/privacy).',
      'You can refuse this collection by resetting or deleting your advertising identifier in device settings — Android: Settings → Privacy → Ads; iOS: Settings → Privacy & Security → Apple Advertising. Refusing leaves every feature of the service available to you, and non-personalized ads may still appear.',
    ],
    note: 'The Operator uses no cookie, advertising identifier, web beacon, or other automatic collection device for any purpose other than serving those ads, and does not itself use collected personal data for ad targeting or profiling. The only purchase the service offers is the optional “buy the developer a coffee” sponsorship, which unlocks nothing; payment is carried out by the Apple App Store or Google Play, and the Operator processes only the purchase record described in Articles 3, 5, 7, and 8.',
  },
  CHILDREN,
  SECURITY,
  OFFICER,
  REMEDIES,
  changes(EFFECTIVE_DATE, [
    'August 26, 2026 — profile photos and the photographs attached to support messages moved from the Operator’s own server disk to Cloudflare R2 object storage. Articles 6 and 7 were amended accordingly: image files are now held by a processor outside Korea. What is collected, why, and how long it is kept did not change',
    'August 26, 2026 — the support screen moved out of the app to a page at cottonray.com/support which the app opens inside itself. What is collected, why, and how long it is kept did not change',
    'August 24, 2026 — updated the purposes, items collected, and retention periods following the addition of photo attachments (up to three, chosen by you) and an optional email address to the in-app support screen, which the Operator uses to send a single reply from no-reply@cottonray.com',
  'August 24, 2026 — updated the purposes, items collected, retention periods, entrustment, and notice of transfers abroad following the introduction of the optional “buy the developer a coffee” sponsorship (in-app purchase; receipts verified by RevenueCat)',
    'August 6, 2026 — updated the items processed, the processors, and transfers abroad following the addition of sign-in methods (username and password, Google, Apple)',
    'August 10, 2026 — updated the purposes, items, and retention periods following the addition of the community (posts, comments, reports, blocks) and the choice of exam type (written or practical) for reminders',
    'August 10, 2026 — updated the automatic collection, entrustment, and transfer-abroad clauses following the introduction of banner ads (Google AdMob, non-personalized)',
    'August 15, 2026 — established (structured on Article 30 of the Personal Information Protection Act, including notice of transfers abroad)',
    'August 20, 2026 — updated the purposes, items collected, retention periods, and how to exercise your rights following the introduction of in-app support messages (feedback only, no contact details taken)',
    'August 19, 2026 — the site language changed to English and this policy was reissued in English. Nothing about how data is handled changed',
  ]),
]

export const SIHEOMBOSO_POLICY: PolicyDocument = {
  slug: 'siheomboso',
  title: '시험보소 (Siheomboso) Privacy Policy',
  intro: `${OPERATOR}, establishes and publishes the following privacy policy for 시험보소 (Siheomboso, “the service”) under Article 30 of the Personal Information Protection Act of the Republic of Korea, in order to protect the personal data of its users and to deal promptly and smoothly with any grievance arising from it.`,
  effectiveDate: EFFECTIVE_DATE,
  lastRevisedDate: LAST_REVISED,
  sections: SECTIONS,
}
